import React, { useState } from 'react';
import { ArrowRight, ExternalLink } from 'lucide-react';

const CategoryCard = ({ category, categoryKey, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div 
      className="category-card group relative border-b border-gray-200 bg-white overflow-hidden"
      data-testid={`category-card-${categoryKey}`}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Gold accent on left on hover */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#C9A227] transform scale-y-0 group-hover:scale-y-100 transition-transform duration-400 origin-top z-30" />

      {/* Main Content Area - White background on hover */}
      <div className="relative">
        {/* White overlay on hover for main content */}
        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Title */}
            <div className="lg:col-span-4">
              <h3 
                className="text-2xl md:text-3xl font-light text-black mb-2 transition-colors duration-300"
              >
                {category.title}
              </h3>
              <p className="text-gray-500 text-sm transition-colors duration-300">{category.subtitle}</p>
            </div>

            {/* Items */}
            <div className="lg:col-span-5">
              <ul className="space-y-3">
                {category.items.map((item, idx) => {
                  const isLinked = typeof item === 'object';
                  const label = isLinked ? item.label : item;
                  const href = isLinked ? item.link : null;
                  const isExternal = href && href.startsWith('http');

                  return (
                    <li key={idx} className="flex items-center gap-3 text-sm md:text-base transition-colors duration-300">
                      <span className="w-1.5 h-1.5 bg-[#C9A227] rounded-full flex-shrink-0" />
                      {href ? (
                        <a
                          href={href}
                          target={isExternal ? '_blank' : '_self'}
                          rel={isExternal ? 'noopener noreferrer' : ''}
                          className="text-gray-700 hover:text-[#C9A227] transition-colors"
                        >
                          {label}
                        </a>
                      ) : (
                        <span className="text-gray-700">{label}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Action */}
            <div className="lg:col-span-3 flex flex-col items-start lg:items-end gap-4">
              {category.link && (
                <a
                  href={category.link}
                  target={category.external ? '_blank' : '_self'}
                  rel={category.external ? 'noopener noreferrer' : ''}
                  className="group/link flex items-center gap-2 text-[#1e3a5f] hover:text-[#C9A227] transition-colors"
                  data-testid={`category-link-${categoryKey}`}
                >
                  <span className="text-sm font-medium tracking-wide uppercase">
                    {category.external ? 'Visit Platform' : 'Explore'}
                  </span>
                  <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  {category.external && <ExternalLink className="w-3 h-3" />}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown Area - Light corporate style */}
      {isExpanded && category.dropdown && (
        <div 
          className="relative bg-gray-50"
          data-testid={`category-dropdown-${categoryKey}`}
        >
          {/* Gold divider line */}
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C9A227]/40 to-transparent" />
          
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {category.dropdown.map((item, idx) => {
                const isLinked = typeof item === 'object';
                const label = isLinked ? item.label : item;
                const href = isLinked ? item.link : null;
                const isExternal = href && href.startsWith('http');

                return href ? (
                  <a
                    key={idx}
                    href={href}
                    target={isExternal ? '_blank' : '_self'}
                    rel={isExternal ? 'noopener noreferrer' : ''}
                    className="group/item p-5 bg-white border border-gray-200 hover:border-[#C9A227] hover:shadow-md transition-all duration-300"
                  >
                    <span className="text-gray-700 text-sm group-hover/item:text-[#1e3a5f] transition-colors">{label}</span>
                  </a>
                ) : (
                  <div
                    key={idx}
                    className="p-5 bg-white border border-gray-200"
                  >
                    <span className="text-gray-700 text-sm">{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CategorySection = ({ categories, region }) => {
  const categoryKeys = Object.keys(categories);

  return (
    <section className="bg-white" data-testid="category-section">
      {/* Section Header */}
      <div className="py-6 bg-[#1e3a5f]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-[2px] bg-[#C9A227]" />
            <h2 className="text-white text-xs tracking-[0.3em] uppercase font-medium">
              Core Services
            </h2>
          </div>
        </div>
      </div>

      {/* Cards */}
      {categoryKeys.map((key, index) => (
        <CategoryCard 
          key={`${region}-${key}`}
          category={categories[key]}
          categoryKey={key}
          index={index}
        />
      ))}
    </section>
  );
};

export default CategorySection;
